import {BaseComponent} from '../base/component.base.js';
import template from './section-item-editor.template.html';
import styles from './section-item-editor.styles.css';
import pageFormStyles from '../page-form/page-form.styles.css';
import {
    awaitsImagePreparation,
    markFocalPoint,
    renderFieldGroup,
    syncFieldCounters,
    syncRequiredIndicator,
} from '../page-form/field-renderers.js';
import {showExistingAsset} from '../page-form/existing-asset.js';
import {FieldReferenceControls, syncFieldGroupVisibility} from '../page-form/field-references.js';
import {
    FieldType,
    TABLE_ROW_ID_KEY,
    imageCompanionKeys,
    isFileFieldType,
    isTruthyFlag,
} from '../../core/vocabulary.js';

/** @type {CSSStyleSheet | null} */
let pageFormSheet = null;

/**
 * The page-form stylesheet as one constructable sheet, parsed on first use and shared by every
 * item editor, so item fields look like the page form's fields without a CSS parse per item.
 * @returns {CSSStyleSheet}
 */
function sharedPageFormSheet() {
    if (!pageFormSheet) {
        pageFormSheet = new CSSStyleSheet();
        pageFormSheet.replaceSync(pageFormStyles);
    }
    return pageFormSheet;
}

const itemSheet = new CSSStyleSheet();
itemSheet.replaceSync(styles);

/**
 * @typedef {import('../../shell/types/user.types.js').TemplateField} TemplateField
 * @typedef {{fieldKey: string, file: File}} PendingItemFile
 */

/**
 * Fields of one item of a repeatable composition section. Each item editor has its own shadow
 * root, so control names and ids stay unique per item and the field references of the item
 * resolve within it. The section editor reads the item through `item` and `files`; it sets
 * `data-list-key` and `data-item-id` on the host.
 *
 * Required rules depend on the item's layout, so no control is natively required: the section
 * editor shows indicators through {@link setRequiredFields} and checks the item's content with
 * the composition core rules, probing values through {@link isFilled}.
 *
 * Emits bubbling, composed `input` and `change` events for every edit, and `asset-request` /
 * `asset-open-request` for stored assets, like the page form. An `input` or `change` that does
 * not cross the shadow boundary by itself is re-dispatched from the host in a microtask, after
 * the controls' own listeners (dependent fields, the remove action)
 * have run. A photo still being prepared for upload is not forwarded: its input reports the
 * change again once the prepared file is in place. Asset requests of an editor that is not in
 * the document yet are dispatched once it is connected.
 */
export class SectionItemEditor extends BaseComponent {
    /** @type {TemplateField[]} */
    #fields = [];
    /** @type {Object<string, *>} Values the fields are rendered with */
    #values = {};
    /** @type {Set<string> | null} Keys the item's layout shows; null shows every field */
    #visibleKeys = null;
    /** @type {Set<string>} */
    #requiredKeys = new Set();
    /** @type {CustomEvent[]} */
    #heldEvents = [];
    /** True while the editor itself re-applies pending files, which is not an edit. */
    #applyingFiles = false;
    #fieldReferences = new FieldReferenceControls(this.shadowRoot, {manageRequired: false});

    constructor() {
        super();
        // The template has no `data-i18n` nodes, so no locale listener is needed.
        this.loadTemplate(template, '', {watchLocale: false});
        this.shadowRoot.adoptedStyleSheets = [sharedPageFormSheet(), itemSheet, ...this.shadowRoot.adoptedStyleSheets];
        // Native `change` events and programmatic `input` events are not composed, so they
        // would stop at this shadow root; the page form listens for both. Capturing also sees
        // the non-bubbling `change` an image drop dispatches.
        // The image-or-text choice updates its groups on these events too, so layout visibility
        // is re-applied before the host event goes out.
        const forward = (event) => {
            // A photo still being prepared reports its change again once prepared.
            if (awaitsImagePreparation(event.target)) return;
            const redispatch = !event.composed && !this.#applyingFiles;
            queueMicrotask(() => {
                this.#syncVisibility();
                if (redispatch) this.#dispatch(event.type);
            });
        };
        this.shadowRoot.addEventListener('input', forward, true);
        this.shadowRoot.addEventListener('change', forward, true);
    }

    connectedCallback() {
        const held = this.#heldEvents;
        this.#heldEvents = [];
        held.forEach((event) => this.dispatchEvent(event));
    }

    /**
     * Renders the item fields. Values typed so far and pending files are kept.
     * @param {TemplateField[]} value
     */
    set fields(value) {
        let files = [];
        if (this.#fields.length > 0) {
            this.#values = this.item;
            files = this.files;
        }
        this.#fields = Array.isArray(value) ? value : [];
        this.#render();
        this.files = files;
    }

    /** @returns {TemplateField[]} The item fields as last set */
    get fields() {
        return this.#fields;
    }

    /**
     * Fills the fields with stored item values: stored assets are shown (through
     * `asset-request`) and the stored focal point is selected. Keys the item does not hold keep
     * their defaults; pending files are dropped.
     * @param {Object<string, *>} value
     */
    set item(value) {
        this.#values = {...value};
        this.#render();
    }

    /**
     * The item as the page submits it: `_id`, then every field in declaration order as a trimmed
     * string, each image followed by its alt text and focal point. An image or document holds
     * its stored file name, or `""` when there is none or it was removed; a pending file does
     * not change it (see {@link files}).
     * @returns {Object<string, string>}
     */
    get item() {
        const item = {[TABLE_ROW_ID_KEY]: this.#itemId()};
        for (const field of this.#fields) {
            item[field.key] = this.#value(field);
            if (field.type === FieldType.IMAGE) {
                for (const key of imageCompanionKeys(field.key)) {
                    item[key] = this.#control(key).value.trim();
                }
            }
        }
        return item;
    }

    /** @returns {PendingItemFile[]} Files chosen for image and document fields, not uploaded yet */
    get files() {
        return this.#fields
            .filter((field) => isFileFieldType(field.type))
            .map((field) => ({fieldKey: field.key, file: this.#control(field.key).files[0]}))
            .filter((pending) => pending.file);
    }

    /**
     * Chooses pending files again, for example for a duplicated item. Entries for unknown or
     * non-file fields are ignored.
     * @param {PendingItemFile[]} value
     */
    set files(value) {
        for (const {fieldKey, file} of Array.isArray(value) ? value : []) {
            const field = this.#fields.find((candidate) => candidate.key === fieldKey);
            if (!isFileFieldType(field?.type)) continue;
            const transfer = new DataTransfer();
            transfer.items.add(file);
            const input = this.#control(fieldKey);
            input.files = transfer.files;
            this.#applyingFiles = true;
            input.dispatchEvent(new Event('change'));
            this.#applyingFiles = false;
        }
        this.#syncVisibility();
    }

    /**
     * Shows only the fields of the item's selected layout.
     * @param {Iterable<string>} keys
     */
    setVisibleFields(keys) {
        this.#visibleKeys = new Set(keys);
        this.#syncVisibility();
    }

    /**
     * Marks the fields the item's layout requires. Indicators only: no control becomes
     * natively required.
     * @param {Iterable<string>} keys
     */
    setRequiredFields(keys) {
        this.#requiredKeys = new Set(keys);
        this.#syncRequiredIndicators();
    }

    /**
     * Whether a field holds a value: text that is not blank, a checked checkbox, or a stored or
     * pending file.
     * @param {string} key
     * @returns {boolean}
     */
    isFilled(key) {
        const input = this.#control(key);
        if (!input) return false;
        if (input.type === 'file') return input.files.length > 0 || Boolean(input.dataset.existingPath);
        if (input.type === 'checkbox') return input.checked;
        return input.value.trim().length > 0;
    }

    /**
     * Moves focus to a field: an image card opens and its header takes focus.
     * @param {string} key
     */
    focusField(key) {
        const group = this.#group(key);
        if (!group) return;
        group.querySelector('.image-card-header')?.setAttribute('aria-expanded', 'true');
        group.querySelector('.image-card-header, input:not([type="hidden"]), textarea, select').focus();
    }

    // ---- rendering ---------------------------------------------------------------------

    #render() {
        const container = this.$('#item-fields');
        container.replaceChildren();
        this.#heldEvents = [];
        const context = {
            fieldKeys: this.#fields.map((field) => field.key),
            onFocalPointChange: () => this.#dispatch('change'),
            allowRemove: true,
            plainSlug: true,
        };
        for (const field of this.#fields) {
            container.appendChild(renderFieldGroup(field, context).group);
        }
        this.#applyValues();
        syncFieldCounters(container);
        container.querySelectorAll('[required]').forEach((element) => element.removeAttribute('required'));
        container.querySelectorAll('.required-indicator').forEach((element) => element.remove());
        this.#fieldReferences.sync(this.#fields);
        this.#syncVisibility();
        this.#syncRequiredIndicators();
    }

    #applyValues() {
        for (const field of this.#fields) {
            this.#applyValue(field, this.#values[field.key]);
            if (field.type === FieldType.IMAGE) {
                const [alt, position] = imageCompanionKeys(field.key);
                this.#applyValue({key: alt, type: FieldType.TEXTAREA}, this.#values[alt]);
                this.#applyFocalPoint(position, this.#values[position]);
            }
        }
    }

    #applyValue(field, value) {
        if (value == null) return;
        const input = this.#control(field.key);
        if (isFileFieldType(field.type)) {
            if (value) showExistingAsset(input, String(value), (name, detail) => this.#emit(name, detail));
        } else if (field.type === FieldType.CHECKBOX) {
            input.checked = isTruthyFlag(value);
        } else {
            input.value = String(value);
        }
    }

    #applyFocalPoint(key, value) {
        if (value == null) return;
        this.#control(key).value = String(value);
        markFocalPoint(this.$(`.focal-point-grid[data-for="${CSS.escape(key)}"]`), String(value));
    }

    #syncVisibility() {
        if (!this.#visibleKeys) return;
        for (const group of this.#groups()) {
            group.dataset.layoutVisible = String(this.#visibleKeys.has(group.dataset.fieldKey));
            syncFieldGroupVisibility(group);
        }
    }

    #syncRequiredIndicators() {
        for (const group of this.#groups()) {
            const label = group.querySelector(':scope > label[for], .image-card-label');
            syncRequiredIndicator(label, this.#requiredKeys.has(group.dataset.fieldKey));
        }
    }

    // ---- helpers -----------------------------------------------------------------------

    #itemId() {
        return String(this.#values[TABLE_ROW_ID_KEY] ?? '') || this.dataset.itemId || '';
    }

    #value(field) {
        const input = this.#control(field.key);
        if (isFileFieldType(field.type)) return input.dataset.existingPath ?? '';
        if (field.type === FieldType.CHECKBOX) return input.checked ? 'true' : '';
        return input.value.trim();
    }

    #control(key) {
        return this.$(`[name="${CSS.escape(key)}"]`);
    }

    #group(key) {
        return this.$(`.field-group[data-field-key="${CSS.escape(key)}"]`);
    }

    #groups() {
        return this.$$('#item-fields > .field-group');
    }

    #dispatch(type) {
        this.dispatchEvent(new Event(type, {bubbles: true, composed: true}));
    }

    #emit(name, detail) {
        const event = new CustomEvent(name, {bubbles: true, composed: true, detail});
        if (this.isConnected) {
            this.dispatchEvent(event);
        } else {
            this.#heldEvents.push(event);
        }
    }
}

customElements.define('section-item-editor', SectionItemEditor);
